import React, { useState, ChangeEvent, FormEvent } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { ContactFormData } from '../types';
import Drift from './objects/Drift';
import ObjectFloat from './objects/ObjectFloat';
import { submitContactForm } from '../services/api';
import '../styles/Contact.css';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    fullName: '',
    email: '',
    countryCode: '',
    phone: '',
    message: '',
    consent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({ ...prev, [name]: target.checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await submitContactForm(formData);
      setSubmitStatus('success');
      setFormData({ fullName: '', email: '', countryCode: '', phone: '', message: '', consent: false });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section contact">
      <div className="obj-spot spot-ring" aria-hidden="true"><Drift><ObjectFloat slug="ring" /></Drift></div>
      <div className="container">
        <div className="section-head" data-reveal>
          <p className="section-eyebrow">Contact</p>
          <h2 className="section-title">
            Don&apos;t hesitate to contact us if you have any questions.
          </h2>
        </div>

        <div className="contact-layout">
          <aside className="contact-aside" data-reveal>
            <ul className="contact-channels">
              <li>
                <Mail size={18} aria-hidden="true" />
                <div>
                  <span className="channel-label">Email</span>
                  <a href="mailto:hello@bulgebracket.co">hello@bulgebracket.co</a>
                </div>
              </li>
              <li>
                <Phone size={18} aria-hidden="true" />
                <div>
                  <span className="channel-label">Phone</span>
                  <a href="tel:+447466152039">+44 7466 152039</a>
                </div>
              </li>
              <li>
                <MapPin size={18} aria-hidden="true" />
                <div>
                  <span className="channel-label">Office</span>
                  <span>London, United Kingdom</span>
                </div>
              </li>
            </ul>
          </aside>

          <form className="contact-form" onSubmit={handleSubmit} data-reveal>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="fullName">Full name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  autoComplete="name"
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="phone">Phone (optional)</label>
              <div className="phone-group">
                <input
                  type="text"
                  id="countryCode"
                  name="countryCode"
                  value={formData.countryCode || ''}
                  onChange={handleInputChange}
                  placeholder="+44"
                  aria-label="Country code"
                  className="phone-code"
                />
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone || ''}
                  onChange={handleInputChange}
                  autoComplete="tel-national"
                  className="phone-number"
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="message">Your situation</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={6}
                placeholder="Second-year at a semi-target, aiming for 2027 S&T summer internships…"
                required
              />
            </div>

            <div className="form-consent">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleInputChange}
                required
              />
              <label htmlFor="consent">I agree to be contacted about my enquiry.</label>
            </div>

            <button type="submit" className="btn btn-primary form-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Send enquiry'}
            </button>

            <div aria-live="polite">
              {submitStatus === 'success' && (
                <p className="form-status form-status-success">
                  Received. Watch your inbox.
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="form-status form-status-error">
                  That didn’t send. Email us instead at hello@bulgebracket.co.
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
